import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "@hp/api-client";
import { Button, Card, CardBody, Input } from "@hp/ui";
import { AppBrandMark } from "../components/layout/AppBrandMark";
import { SESSION_ENDED_KEY, useAuth } from "../auth/AuthContext";
import { USE_MOCK_DATA } from "../api/config";
import { DEMO_CREDENTIALS } from "../mocks/authStore";

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  // Set by the auth context when a sign-in ended on its own (expired or revoked): said once, then forgotten.
  const [sessionEnded] = React.useState(() => {
    try {
      const ended = sessionStorage.getItem(SESSION_ENDED_KEY) === "1";
      sessionStorage.removeItem(SESSION_ENDED_KEY);
      return ended;
    } catch {
      return false;
    }
  });

  if (user) {
    const from = (location.state as { from?: Location } | null)?.from;
    return <Navigate to={from?.pathname ?? "/"} replace />;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in - please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <AppBrandMark variant="login" linkToHome={false} />
          <p className="mt-4 text-center text-sm text-zinc-500">
            Sign in with the account your admin set up for you.
          </p>
        </div>

        <Card>
          <CardBody>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium text-zinc-700">Email</span>
                <Input
                  type="email"
                  required
                  autoFocus
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@hindustanpower.com"
                />
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium text-zinc-700">Password</span>
                <Input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                />
              </label>

              {sessionEnded && !error ? (
                <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Your session has ended. Please sign in again.
                </p>
              ) : null}

              {error ? (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
              ) : null}

              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </CardBody>
        </Card>

        {USE_MOCK_DATA ? (
          <div className="mt-4 rounded-lg border border-dashed border-zinc-300 bg-white/60 p-4 text-xs text-zinc-500">
            <p className="mb-2 font-medium text-zinc-600">Demo accounts (no backend required):</p>
            <ul className="flex flex-col gap-1">
              {DEMO_CREDENTIALS.map((account) => (
                <li key={account.email} className="flex justify-between gap-3">
                  <span>
                    {account.email} <span className="text-zinc-400">({account.role})</span>
                  </span>
                  <span className="font-mono text-zinc-400">{account.password}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
